import { ExternalLinkIcon } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useEffectEvent, useRef, useState } from 'react';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import type { InAppBrowserApp } from '@/lib/posthog/inAppBrowser';
import type { LinkedInLoginProps } from '@/modules/auth/components/LinkedInLogin';
import {
  buildHandoffUrl,
  isHandoffFailed,
  type MobileOs,
} from '@/modules/auth/inAppHandoff';
import { authButtonClickedEvents } from '@/modules/auth/types/externalLogin';
import { Button } from '@/modules/ui/components/button/Button';

import { InAppBrowserSheet } from './inAppBrowserSheet/InAppBrowserSheet';

/** How long Android gets to hide the page by opening the browser. */
const ANDROID_HANDOFF_MS = 1500;

type FallbackReason = 'cannotDetect' | 'stillVisible' | 'fallbackLoaded';

const assignLocation = (url: string) => {
  window.location.href = url;
};

interface InAppGoogleSignUpProps {
  app: InAppBrowserApp;
  os: MobileOs;
  /** Offered in the sheet as a way to sign up without leaving the app. */
  linkedIn?: LinkedInLoginProps;
  /** The page's query string; stories set it to open as Android's fallback page does. */
  search?: string;
  /** Opens the hand-off URL; stories replace it so the page stays put. */
  navigate?: (url: string) => void;
}

/**
 * Google's button for `/sign-up` inside an app's built-in browser, where
 * Google blocks sign-in. The tap reopens the page in the real browser, whose
 * Google button then shows One Tap; the sheet covers when that can't happen.
 * iOS can't tell whether Safari opened, so it shows the sheet every time.
 */
export const InAppGoogleSignUp = ({
  app,
  os,
  linkedIn,
  search = window.location.search,
  navigate = assignLocation,
}: InAppGoogleSignUpProps) => {
  const posthog = usePostHog();
  const trackEvent = useTrackEvent();
  // Why the sheet is open, or null when closed. Android's intent fallback
  // reloads the page here, marked, when no browser took it (`buildHandoffUrl`).
  const [sheetReason, setSheetReason] = useState<FallbackReason | null>(() =>
    isHandoffFailed(search) ? 'fallbackLoaded' : null,
  );
  // Stops the Android check of the latest attempt (timer and listener).
  const cancelAndroidCheck = useRef<(() => void) | null>(null);

  const reportFallbackShown = useEffectEvent((reason: FallbackReason) =>
    trackEvent('escapeFallbackShown', { os, reason }),
  );
  // Only a closed to open change reports: `showSheet` keeps the first reason
  // while the sheet is open, so Try again and late timers don't count again.
  useEffect(() => {
    if (sheetReason) reportFallbackShown(sheetReason);
  }, [sheetReason]);

  useEffect(() => () => cancelAndroidCheck.current?.(), []);

  const showSheet = (reason: FallbackReason) =>
    setSheetReason((current) => current ?? reason);

  const handOff = (retry: boolean) => {
    if (os === 'other') {
      showSheet('cannotDetect');
      return;
    }

    trackEvent('escapeAttempted', { os, retry });
    cancelAndroidCheck.current?.();

    // Opening the browser hides this page; still visible means it didn't.
    // The listener goes up first so a fast hide isn't missed.
    if (os === 'android') {
      let didLeave = false;
      const onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') didLeave = true;
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      const cancel = () => {
        window.clearTimeout(timer);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        cancelAndroidCheck.current = null;
      };
      const timer = window.setTimeout(() => {
        cancel();
        if (!didLeave) showSheet('stillVisible');
      }, ANDROID_HANDOFF_MS);
      cancelAndroidCheck.current = cancel;
    }

    navigate(
      buildHandoffUrl(window.location.href, os, posthog.get_distinct_id()),
    );

    if (os === 'ios') showSheet('cannotDetect');
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => {
          trackEvent(authButtonClickedEvents.signUp, { authMethod: 'Google' });
          handOff(false);
        }}
        // Google's own button border and hover, so it reads as Google's.
        className="h-12 w-full gap-3 rounded-lg border-[#dadce0] px-3.5 shadow-none hover:bg-[#f8f9fa] hover:text-inherit active:scale-[0.98] has-[>svg]:px-3.5"
      >
        <GoogleIcon />
        <span className="flex flex-1 flex-col items-center leading-tight">
          <span className="text-foreground text-[15px] font-medium">
            Sign up with Google
          </span>
          <span className="text-muted-foreground text-xs">
            {os === 'ios' ? 'Opens in Safari' : 'Opens in your browser'}
          </span>
        </span>
        <ExternalLinkIcon
          className="text-muted-foreground size-[18px]"
          aria-hidden
        />
      </Button>
      <InAppBrowserSheet
        open={sheetReason !== null}
        onOpenChange={(open) => {
          if (!open) setSheetReason(null);
        }}
        app={app}
        os={os}
        onRetry={() => handOff(true)}
        linkedIn={
          linkedIn && {
            ...linkedIn,
            onClick: () =>
              trackEvent('altMethodChosen', { method: 'LinkedIn' }),
          }
        }
      />
    </>
  );
};

/** Google's "G", drawn here because the official button can't render in-app. */
const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="size-[18px] shrink-0" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);
